import type { ClientTableName } from '@app/icare/client/db/types'
import { type AppCommonDatabase, CommonDatabaseSchema } from '@app/icare/db/schema'
import type { AdTeam } from '@app/icare/entities/adTeam/schema/ADTeam'
import type { Article } from '@app/icare/entities/article/schema/Article'
import type { CareService } from '@app/icare/entities/careService/schema/CareService'
import type { Clinic } from '@app/icare/entities/clinic/schema/Clinic'
import type { CooperationPartner } from '@app/icare/entities/cooperationPartner/schema/CooperationPartner'
import type { CostCarrier } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import type { Doctor } from '@app/icare/entities/doctor/schema/Doctor'
import type { Hospice } from '@app/icare/entities/hospice/schema/Hospice'
import type { MedicalSupplyHome } from '@app/icare/entities/medicalSupplyHome/schema/MedicalSupplyHome'
import type { MedicalTechnology } from '@app/icare/entities/medicalTechnology/schema/MedicalTechnology'
import type { NursingHome } from '@app/icare/entities/nursingHome/schema/NursingHome'
import type { Patient } from '@app/icare/entities/patient/schema/Patient'
import type { PatientADTeamRelation } from '@app/icare/entities/patient/schema/PatientADTeam'
import type { PatientAddressRelation } from '@app/icare/entities/patient/schema/PatientAddress'
import type { PatientApplicantRelation } from '@app/icare/entities/patient/schema/PatientApplicant'
import type { PatientCareLevelRelation } from '@app/icare/entities/patient/schema/PatientCareLevel'
import type { PatientCareManagerRelation } from '@app/icare/entities/patient/schema/PatientCareManager'
import type { PatientCareServiceRelation } from '@app/icare/entities/patient/schema/PatientCareService'
import type { PatientClinicRelation } from '@app/icare/entities/patient/schema/PatientClinic'
import type { PatientCooperationPartnerRelation } from '@app/icare/entities/patient/schema/PatientCooperationPartner'
import type { PatientCostCarrierRelation } from '@app/icare/entities/patient/schema/PatientCostCarrier'
import type { PatientCostCarrierExemption } from '@app/icare/entities/patient/schema/PatientCostCarrierExemption'
import type { PatientDoctorRelation } from '@app/icare/entities/patient/schema/PatientDoctor'
import type { PatientMedicalSupplyAccessRelation } from '@app/icare/entities/patient/schema/PatientMedicalSupplyAccess'
import type { PatientMedicalTechnologyRelation } from '@app/icare/entities/patient/schema/PatientMedicalTechnology'
import type { PatientMobilityRelation } from '@app/icare/entities/patient/schema/PatientMobility'
import type { PatientPharmacyRelation } from '@app/icare/entities/patient/schema/PatientPharmacy'
import type { PatientRelativeRelation } from '@app/icare/entities/patient/schema/PatientRelative'
import type { PatientSeparateResidenceRelation } from '@app/icare/entities/patient/schema/PatientSeparateResidence'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import type { PatientWeightRelation } from '@app/icare/entities/patient/schema/PatientWeight'
import type { PatientFindingView } from '@app/icare/entities/patientFinding/schema/PatientFinding'
import { PatientFindingViewSchema } from '@app/icare/entities/patientFinding/schema/PatientFinding'
import type { PatientForm } from '@app/icare/entities/patientForm/schema/PatientForm'
import type { PatientNotification } from '@app/icare/entities/patientNotification/schema/PatientNotification'
import type { Pharmacy } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import type { SyncableUser } from '@app/icare/entities/user/schema/User'
import type { EntityId } from '@pkg/db/schema'
import {
  type AppMetadata,
  AppMetadataSchema,
  type SyncClientDatabase,
  type SyncMessage,
  SyncMessageSchema,
} from '@pkg/sync/common'

export const DatabaseSchema = {
  ...CommonDatabaseSchema,
  message: SyncMessageSchema,
  appMetadata: AppMetadataSchema,
  patientFindingsView: PatientFindingViewSchema,
} as const

/**
 * Manually spelled out to avoid mapped-type z.infer<> expansion.
 * See comment in schema.ts for rationale.
 */
export type AppClientDatabase = AppCommonDatabase & {
  message: SyncMessage
  appMetadata: AppMetadata
  patientFindingsView: PatientFindingView
}

type MigrationDatabase = {
  kysely_migration: {
    name: string
    timestamp: string
  }
}

export type ClientDatabase = SyncClientDatabase & AppClientDatabase & MigrationDatabase

export interface Entity {
  patient: Patient
  adTeam: AdTeam
  careService: CareService
  clinic: Clinic
  cooperationPartner: CooperationPartner
  costCarrier: CostCarrier
  doctor: Doctor
  user: SyncableUser
  hospice: Hospice
  medicalSupplyHome: MedicalSupplyHome
  medicalTechnology: MedicalTechnology
  nursingHome: NursingHome
  pharmacy: Pharmacy
  article: Article
  patientForm: PatientForm
  patientNotification: PatientNotification
}

export interface PatientEntityRelation {
  patientAddress: PatientAddressRelation
  patientMobility: PatientMobilityRelation
  patientWeight: PatientWeightRelation
  patientCareLevel: PatientCareLevelRelation
  patientCareService: PatientCareServiceRelation
  patientRelative: PatientRelativeRelation
  patientTherapy: PatientTherapyRelation
  patientApplicant: PatientApplicantRelation
  patientPharmacy: PatientPharmacyRelation
  patientMedicalTechnology: PatientMedicalTechnologyRelation
  patientCooperationPartner: PatientCooperationPartnerRelation
  patientCostCarrier: PatientCostCarrierRelation
  patientCostCarrierExemption: PatientCostCarrierExemption
  patientSeparateResidence: PatientSeparateResidenceRelation
  patientClinic: PatientClinicRelation
  patientDoctor: PatientDoctorRelation
  patientMedicalSupplyAccess: PatientMedicalSupplyAccessRelation
  patientAdTeam: PatientADTeamRelation
  patientCareManager: PatientCareManagerRelation
}

const patientRelationTableNameSet: Record<keyof PatientEntityRelation, true> = {
  patientAddress: true,
  patientMobility: true,
  patientWeight: true,
  patientCareLevel: true,
  patientCareService: true,
  patientRelative: true,
  patientTherapy: true,
  patientApplicant: true,
  patientPharmacy: true,
  patientMedicalTechnology: true,
  patientCooperationPartner: true,
  patientCostCarrier: true,
  patientCostCarrierExemption: true,
  patientSeparateResidence: true,
  patientClinic: true,
  patientDoctor: true,
  patientMedicalSupplyAccess: true,
  patientAdTeam: true,
  patientCareManager: true,
}

export const patientRelationTableNames = Object.keys(patientRelationTableNameSet) as (keyof PatientEntityRelation)[]

export type AnyRelation = PatientEntityRelation[keyof PatientEntityRelation]

export type EntityName = keyof Entity

export type AppEntity<N extends EntityName> = Entity[N]

export type PatientRelationTable = keyof PatientEntityRelation
/**
 * Extract the target entity table name from a relation's toId.
 * Intersected with ClientTableName so TypeScript accepts it as a valid table constraint.
 *
 * E.g. RelationTargetTable<'patientPharmacy'> = 'pharmacy'
 */
export type RelationTargetTable<T extends keyof PatientEntityRelation> = PatientEntityRelation[T] extends {
  toId: EntityId<infer E>
}
  ? E & ClientTableName
  : never

export const MasterDataEntityName = {
  adTeam: 'adTeam',
  careService: 'careService',
  clinic: 'clinic',
  cooperationPartner: 'cooperationPartner',
  costCarrier: 'costCarrier',
  doctor: 'doctor',
  hospice: 'hospice',
  medicalSupplyHome: 'medicalSupplyHome',
  medicalTechnology: 'medicalTechnology',
  nursingHome: 'nursingHome',
  pharmacy: 'pharmacy',
} as const

export type MainDataEntityNameType = (typeof MasterDataEntityName)[keyof typeof MasterDataEntityName]

export type MasterDataEntity = {
  [K in MainDataEntityNameType]: Entity[K]
}

export type AnyMasterDataEntity = MasterDataEntity[keyof MasterDataEntity]
